// Tab Quy trình duyệt: phễu tạo → gửi duyệt → ban hành, thời gian từng bước, phân bố thời gian duyệt so với mục tiêu,
// tuổi hồ sơ đang chờ, hiệu suất từng người duyệt, lý do từ chối gần đây.
import { Button, Table, Tooltip } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight } from 'lucide-react';
import { SLA_DAYS, relTime } from '../stats';
import type { approvalAnalytics } from '../analytics';
import { docLink, fmtDateTime } from '../utils';
import { COLORS, Card, Legend, RateBar, Stat, StatGrid, ThinDataHint, fmtDays, pct, type DashCtx } from './ui';

export default function ApprovalTab({ ctx, data }: { ctx: DashCtx; data: ReturnType<typeof approvalAnalytics> }) {
  const navigate = useNavigate();
  const maxFunnel = Math.max(1, ...data.funnel.map(f => f.count));
  const maxStep = Math.max(0.01, ...data.steps.map(s => s.days));
  const oldest = data.pendingDocs[0];

  return (
    <>
      <ThinDataHint count={data.decisions} ctx={ctx} what="lượt duyệt / từ chối" />
      <StatGrid>
        <Stat label="Thời gian duyệt TB" value={data.approved ? fmtDays(data.avgDays) : '—'} tone={data.avgDays > SLA_DAYS ? 'text-red-500' : 'text-slate-800'}
          sub={data.approved ? `trung vị ${fmtDays(data.medianDays)} · P90 ${fmtDays(data.p90Days)}` : 'chưa có lượt duyệt'} info="Tính từ lần gửi duyệt gần nhất đến lúc ban hành." />
        <Stat label={`Đạt mục tiêu ≤ ${SLA_DAYS} ngày`} value={data.approved ? pct(data.sla) : '—'} tone={data.sla >= 0.9 ? 'text-emerald-600' : data.sla >= 0.7 ? 'text-amber-600' : 'text-red-500'} sub={`${data.approved} lượt ban hành trong kỳ`} />
        <Stat label="Tỷ lệ từ chối" value={data.decisions ? pct(data.rejectRate) : '—'} sub={`${data.rejected}/${data.decisions} quyết định`} />
        <Stat label="Duyệt ngay lần đầu" value={data.approved ? pct(data.firstPass) : '—'} sub="không bị trả lại lần nào" info="Tỷ lệ tài liệu được ban hành mà chưa từng bị từ chối — đo chất lượng hồ sơ gửi lên." />
        <Stat label="Đang chờ duyệt" value={data.pendingDocs.length} tone={oldest && oldest.age > 3 ? 'text-red-500' : 'text-slate-800'}
          sub={oldest ? `lâu nhất ${fmtDays(oldest.age)}` : 'không có hồ sơ chờ'} onClick={() => navigate('/document-approval')} />
      </StatGrid>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Phễu xử lý tài liệu" hint="tài liệu tạo trong kỳ">
          <div className="space-y-3">
            {data.funnel.map((f, i) => {
              const conv = i > 0 && data.funnel[i - 1].count ? f.count / data.funnel[i - 1].count : null;
              return (
                <div key={f.key}>
                  <div className="mb-1 flex items-center justify-between text-[13px]">
                    <span className="text-slate-600">{f.label}</span>
                    <span><b className="text-slate-800">{f.count}</b>{conv !== null && <span className="ml-2 text-xs text-slate-400">{pct(conv)} so với bước trước</span>}</span>
                  </div>
                  <div className="h-7 rounded-md bg-slate-50">
                    <div className="h-full rounded-md" style={{ width: `${(f.count / maxFunnel) * 100}%`, background: COLORS.brand, opacity: 1 - i * 0.22 }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-[13px] sm:grid-cols-4">
            {[
              { label: 'Từng bị từ chối', v: data.funnelSide.rejectedOnce, c: 'text-red-500' },
              { label: 'Đang chờ duyệt', v: data.funnelSide.pending, c: 'text-amber-600' },
              { label: 'Còn dự thảo', v: data.funnelSide.draft, c: 'text-slate-600' },
              { label: 'Đang bị trả lại', v: data.funnelSide.rejected, c: 'text-slate-600' },
            ].map(x => (
              <div key={x.label} className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-xs text-slate-500">{x.label}</div><div className={`text-lg font-bold ${x.c}`}>{x.v}</div></div>
            ))}
          </div>
        </Card>

        <Card title="Thời gian trung bình từng bước" info="Soạn thảo: từ lúc tạo đến lần gửi duyệt đầu tiên. Chờ duyệt: từ lúc gửi đến khi ban hành. Sửa sau từ chối: từ lúc bị trả lại đến khi gửi lại.">
          <div className="space-y-4 pt-1">
            {data.steps.map(s => (
              <div key={s.key}>
                <div className="mb-1 flex items-center justify-between text-[13px]">
                  <span className="text-slate-600">{s.label}</span>
                  <span className="font-semibold text-slate-800">{s.n ? fmtDays(s.days) : '—'} <span className="text-xs font-normal text-slate-400">({s.n} lượt)</span></span>
                </div>
                <RateBar value={s.n ? s.days / maxStep : 0} color={s.key === 'wait' && s.days > SLA_DAYS ? COLORS.red : s.key === 'rework' ? COLORS.amber : COLORS.brand} height={10} />
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#ecf6f9] px-3 py-2 text-[13px] text-[#2c6b7f]">
            Tổng chu trình trung bình (tạo → ban hành): <b>{data.cycleCount ? fmtDays(data.cycleDays) : '—'}</b>
            <span className="text-xs text-[#357f96]/70">· {data.cycleCount} tài liệu</span>
          </div>
        </Card>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Phân bố thời gian duyệt" className="lg:col-span-2">
          <Legend items={[{ label: `Trong mục tiêu (≤ ${SLA_DAYS} ngày)`, color: COLORS.green }, { label: 'Vượt mục tiêu', color: COLORS.red }]} />
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.histogram} margin={{ top: 16, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} interval={0} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <ChartTooltip formatter={v => [`${v} lượt`, 'Số lượt duyệt']} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={34} label={{ position: 'top', fontSize: 11, fill: '#64748b' }}>
                  {data.histogram.map(h => <Cell key={h.label} fill={h.overSla ? COLORS.red : COLORS.green} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Hồ sơ đang chờ" hint="theo thời gian chờ" extra={<Button size="small" type="link" onClick={() => navigate('/document-approval')}>Đi tới duyệt <ArrowRight size={13} /></Button>}>
          <div className="mb-3 grid grid-cols-4 gap-1.5 text-center">
            {data.aging.map((a, i) => (
              <div key={a.label} className={`rounded-lg px-1 py-2 ${a.count ? (i >= 2 ? 'bg-red-50' : 'bg-amber-50') : 'bg-slate-50'}`}>
                <div className={`text-lg font-bold ${a.count ? (i >= 2 ? 'text-red-600' : 'text-amber-700') : 'text-slate-300'}`}>{a.count}</div>
                <div className="text-[11px] text-slate-500">{a.label}</div>
              </div>
            ))}
          </div>
          <ul className="divide-y divide-slate-100">
            {data.pendingDocs.slice(0, 5).map(x => (
              <li key={x.doc.id} className="flex items-center gap-2 py-2 text-[13px]">
                <div className="min-w-0 flex-1">
                  <Link to={docLink(x.doc.id)} className="block truncate font-medium text-slate-700 hover:text-[#357f96]">{x.doc.title}</Link>
                  <div className="text-xs text-slate-400">{x.doc.createdByName} · gửi {fmtDateTime(x.doc.submittedAt)}</div>
                </div>
                <span className={`shrink-0 text-xs font-semibold ${x.age > 3 ? 'text-red-500' : 'text-amber-600'}`}>{fmtDays(x.age)}</span>
              </li>
            ))}
            {data.pendingDocs.length === 0 && <li className="py-4 text-center text-sm text-slate-400">Không có hồ sơ chờ duyệt</li>}
          </ul>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <Card title="Hiệu suất người duyệt" hint="trong kỳ" className="xl:col-span-3">
          <Table
            size="small" rowKey="name" dataSource={data.approvers} pagination={false} scroll={{ x: 560 }}
            locale={{ emptyText: 'Chưa có lượt duyệt trong kỳ' }}
            columns={[
              { title: 'Người duyệt', dataIndex: 'name', render: v => <span className="font-medium text-slate-700">{v}</span> },
              { title: 'Ban hành', dataIndex: 'approved', width: 90, align: 'right', sorter: (a, b) => a.approved - b.approved },
              { title: 'Từ chối', dataIndex: 'rejected', width: 90, align: 'right', render: (v, r) => <span>{v} <span className="text-xs text-slate-400">({r.total ? pct(v / r.total) : '0%'})</span></span> },
              { title: 'TG xử lý TB', dataIndex: 'avgDays', width: 110, align: 'right', sorter: (a, b) => a.avgDays - b.avgDays, render: v => <span className={v > SLA_DAYS ? 'font-medium text-red-500' : ''}>{fmtDays(v)}</span> },
              {
                title: 'Đạt mục tiêu', dataIndex: 'sla', width: 140,
                render: v => <Tooltip title={`${pct(v)} lượt xử lý trong ${SLA_DAYS} ngày`}><div className="flex items-center gap-2"><span className="flex-1"><RateBar value={v} color={v >= 0.9 ? COLORS.green : v >= 0.7 ? COLORS.amber : COLORS.red} height={6} /></span><span className="w-9 text-right text-xs">{pct(v)}</span></div></Tooltip>,
              },
            ]}
          />
        </Card>
        <Card title="Lý do từ chối gần đây" className="xl:col-span-2">
          {data.rejections.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Không có lượt từ chối trong kỳ</div> : (
            <ul className="divide-y divide-slate-100">
              {data.rejections.map((e, i) => (
                <li key={`${e.doc.id}-${i}`} className="py-2 text-[13px]">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={docLink(e.doc.id)} className="truncate font-medium text-[#357f96] hover:underline">{e.doc.code}</Link>
                    <span className="shrink-0 text-[11px] text-slate-400">{relTime(e.at)}</span>
                  </div>
                  <div className="text-slate-600">“{e.note || 'Không ghi lý do'}”</div>
                  <div className="text-xs text-slate-400">{e.approver}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
